import { useEffect, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

function formatErr(d) {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d.msg || JSON.stringify(d);
}

// The sign-in pages open at the top, so the form is the first thing seen. (The page a person comes from can be scrolled
// down to its footer, and a short page like this one would open at the bottom.)
function useStartAtTop() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
}

// A friend who came from a DJ party link is sent back to that party after signing in or signing up (the party page leaves its address here).
function backToParty() {
  try {
    const p = sessionStorage.getItem("dtm_dj_next");
    sessionStorage.removeItem("dtm_dj_next");
    return p && /^\/dj\/join\/[A-Za-z0-9_-]{6,40}$/.test(p) ? p : "/";
  } catch (e) { void e; return "/"; }
}

export function Login() {
  useStartAtTop();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await login(email, password); toast.success("Welcome back"); nav(backToParty()); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto py-16" data-testid="login-page">
      <h1 className="font-display text-4xl font-extrabold mb-2">Welcome back</h1>
      <p className="text-zinc-400 mb-8">Sign in to save playlists and favorites.</p>
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="login-email" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-cyan-400/60" />
        <input data-testid="login-password" autoComplete="current-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-cyan-400/60" />
        <button data-testid="login-submit" disabled={busy} className="w-full py-3 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300 disabled:opacity-50">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <p className="text-sm text-zinc-500 mt-4"><Link to="/forgot-password" data-testid="forgot-password-link" className="text-cyan-400">Forgot your password?</Link></p>
      <p className="text-sm text-zinc-500 mt-2">No account? <Link to="/register" className="text-cyan-400">Create one</Link></p>
    </div>
  );
}

// The "are you human" check from Cloudflare (Turnstile). Its script is only loaded when the server says the check is switched on.
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!window.__turnstileLoading) {
    window.__turnstileLoading = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true; s.defer = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = () => { window.__turnstileLoading = null; reject(new Error("Could not load the check")); };
      document.head.appendChild(s);
    });
  }
  return window.__turnstileLoading;
}

function TurnstileBox({ siteKey, onToken, resetCount }) {
  const holder = useRef(null);
  const widget = useRef(null);
  const give = useRef(onToken);
  give.current = onToken;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let dead = false;
    loadTurnstile()
      .then((ts) => {
        if (dead || !holder.current) return;
        widget.current = ts.render(holder.current, {
          sitekey: siteKey, theme: "dark",
          callback: (t) => give.current(t),
          "expired-callback": () => give.current(""),
          "error-callback": () => give.current(""),
        });
      })
      .catch(() => { if (!dead) setFailed(true); });
    return () => {
      dead = true;
      try { if (widget.current != null && window.turnstile) window.turnstile.remove(widget.current); } catch (e) { void e; }
      widget.current = null;
    };
  }, [siteKey]);

  // Each answer works only once, so ask for a fresh one after a sign-up try that did not go through.
  useEffect(() => {
    if (!resetCount || widget.current == null || !window.turnstile) return;
    try { window.turnstile.reset(widget.current); } catch (e) { void e; }
  }, [resetCount]);

  return (
    <div data-testid="human-check">
      <div ref={holder} />
      {failed && <p className="text-sm text-crimson">The human check could not load. Turn off any ad or tracking blocker for this page, then reload.</p>}
    </div>
  );
}

export function Register() {
  useStartAtTop();
  const { register } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [name, setName] = useState(""); const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);                 // what the server says about the human check and email confirmation
  const [humanToken, setHumanToken] = useState("");
  const [resets, setResets] = useState(0);
  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)).catch(() => setCfg({})); }, []);
  const siteKey = cfg?.turnstile_site_key || "";
  const needsCheck = Boolean(siteKey);
  const submit = async (e) => {
    e.preventDefault();
    if (needsCheck && !humanToken) { toast.error("Please finish the human check first."); return; }
    setBusy(true);
    try {
      const u = await register(email, password, name, humanToken || undefined);
      toast.success(u?.email_verified === false ? "Account created. Check your email for a link to confirm it." : "Account created");
      nav(backToParty());
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
      setHumanToken(""); setResets((n) => n + 1);
    } finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto py-16" data-testid="register-page">
      <h1 className="font-display text-4xl font-extrabold mb-2">Create your account</h1>
      <p className="text-zinc-400 mb-8">Free forever. Upgrade to Premium anytime.</p>
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="register-name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Display name"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-cyan-400/60" />
        <input data-testid="register-email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-cyan-400/60" />
        <input data-testid="register-password" autoComplete="new-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 6 chars)"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-cyan-400/60" />
        {needsCheck && <TurnstileBox siteKey={siteKey} onToken={setHumanToken} resetCount={resets} />}
        <button data-testid="register-submit" disabled={busy || (needsCheck && !humanToken)} className="w-full py-3 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300 disabled:opacity-50">
          {busy ? "Creating…" : "Create account"}
        </button>
        {cfg?.email_verification && (
          <p className="text-xs text-zinc-500 text-center" data-testid="register-confirm-note">We will email you a link to confirm your address.</p>
        )}
        <p className="text-xs text-zinc-500 text-center" data-testid="register-agree">
          By creating an account you agree to the <Link to="/terms" className="text-cyan-400 hover:underline">Terms</Link> and <Link to="/privacy" className="text-cyan-400 hover:underline">Privacy Policy</Link>.
        </p>
      </form>
      <p className="text-sm text-zinc-500 mt-6">Have an account? <Link to="/login" className="text-cyan-400">Sign in</Link></p>
    </div>
  );
}

export function ForgotPassword() {
  useStartAtTop();
  const [email, setEmail] = useState(""); const [busy, setBusy] = useState(false); const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto py-16" data-testid="forgot-password-page">
      <h1 className="font-display text-4xl font-extrabold mb-2">Forgot your password?</h1>
      {sent ? (
        <div data-testid="forgot-password-sent">
          <p className="text-zinc-300 mb-4">If there is an account for <span className="text-white font-semibold">{email}</span>, we just sent a link to choose a new password. It works for 60 minutes.</p>
          <p className="text-sm text-zinc-500">Nothing in your inbox? Check your spam folder, or wait a minute and try again.</p>
        </div>
      ) : (
        <>
          <p className="text-zinc-400 mb-8">Enter your email and we'll send you a link to choose a new one.</p>
          <form onSubmit={submit} className="space-y-4">
            <input data-testid="forgot-email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-cyan-400/60" />
            <button data-testid="forgot-submit" disabled={busy} className="w-full py-3 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300 disabled:opacity-50">
              {busy ? "Sending…" : "Send reset link"}
            </button>
          </form>
        </>
      )}
      <p className="text-sm text-zinc-500 mt-6"><Link to="/login" className="text-cyan-400">Back to sign in</Link></p>
    </div>
  );
}

export function ResetPassword() {
  useStartAtTop();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState(""); const [again, setAgain] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (password !== again) { toast.error("The two passwords don't match"); return; }
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, password }); toast.success("Password changed. Please sign in."); nav("/login"); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!token) return (
    <div className="max-w-md mx-auto py-16" data-testid="reset-password-page">
      <h1 className="font-display text-4xl font-extrabold mb-2">Link problem</h1>
      <p className="text-zinc-400 mb-6">This reset link is missing its code. Please request a new one.</p>
      <Link to="/forgot-password" className="text-cyan-400">Send me a new link</Link>
    </div>
  );
  return (
    <div className="max-w-md mx-auto py-16" data-testid="reset-password-page">
      <h1 className="font-display text-4xl font-extrabold mb-2">Choose a new password</h1>
      <p className="text-zinc-400 mb-8">At least 6 characters.</p>
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="reset-password" autoComplete="new-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-cyan-400/60" />
        <input data-testid="reset-password-again" autoComplete="new-password" type="password" required minLength={6} value={again} onChange={(e) => setAgain(e.target.value)} placeholder="Type it again"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-cyan-400/60" />
        <button data-testid="reset-submit" disabled={busy} className="w-full py-3 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300 disabled:opacity-50">
          {busy ? "Saving…" : "Save new password"}
        </button>
      </form>
    </div>
  );
}

// The page the confirmation email links to: /verify-email?token=...
export function VerifyEmail() {
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState(token ? "working" : "missing");   // working | done | error | missing
  const [msg, setMsg] = useState("");
  const started = useRef(false);
  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    api.post("/auth/verify-email", { token })
      .then(() => { setState("done"); refresh(); })
      .catch((err) => { setMsg(formatErr(err.response?.data?.detail)); setState("error"); });
  }, [token, refresh]);
  return (
    <div className="max-w-md mx-auto py-16" data-testid="verify-page">
      {state === "working" && <h1 className="font-display text-3xl font-extrabold" data-testid="verify-working">Confirming your email…</h1>}
      {state === "done" && (
        <div data-testid="verify-done">
          <h1 className="font-display text-4xl font-extrabold mb-2">Email confirmed</h1>
          <p className="text-zinc-400 mb-6">Thank you. Everything on your account is unlocked.</p>
          <Link to="/" className="text-cyan-400 hover:underline">Go to the home page</Link>
        </div>
      )}
      {state === "error" && (
        <div data-testid="verify-error">
          <h1 className="font-display text-4xl font-extrabold mb-2">Link problem</h1>
          <p className="text-zinc-400 mb-6">{msg}</p>
          <Link to="/login" className="text-cyan-400 hover:underline">Go to sign in</Link>
        </div>
      )}
      {state === "missing" && (
        <div data-testid="verify-missing">
          <h1 className="font-display text-4xl font-extrabold mb-2">Link problem</h1>
          <p className="text-zinc-400 mb-6">This link is missing its code. Open the link in your email again, or sign in and ask for a new one.</p>
          <Link to="/login" className="text-cyan-400 hover:underline">Go to sign in</Link>
        </div>
      )}
    </div>
  );
}
